import { useMemo, useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { groupMembersQueryOptions } from '@/utils/recipientQueryOptions'
import type { Group } from '@/types/groupTypes'
import type { UserData } from '@/types/userTypes'
import { recipientLine as buildRecipientLine } from '@/lib/format'
import { canReceiveSMS } from '@/lib/phone'
import type { RosterMember } from '@/types/rosterTypes'
import type { PickerSegment } from './RecipientPicker'

export type GroupChip = { kind: 'group'; id: string; name: string }
export type PersonChip = {
  kind: 'person'
  id: string
  name: string
  phone: string | null
  email: string | null
}
export type Chip = GroupChip | PersonChip

/** One deduped recipient resolved from the chips. */
export interface ResolvedRecipient {
  phoneNumber: string
  displayName: string
  azureAdUserId?: string
  email?: string
}

/**
 * Chip state for a "To" field: whole groups and individuals added from one
 * search, with every group chip's members resolved and deduped into a single
 * recipient list. Shared by the broadcast composer and the new-group-chat
 * dialog so both screens search, scroll and count identically.
 */
export function useRecipientChips() {
  const [chips, setChips] = useState<Chip[]>([])
  const [query, setQuery] = useState('')
  const [segment, setSegment] = useState<PickerSegment>('all')
  const [pickerOpen, setPickerOpen] = useState(false)

  const groupChips = useMemo(() => chips.filter((c): c is GroupChip => c.kind === 'group'), [chips])
  const personChips = useMemo(
    () => chips.filter((c): c is PersonChip => c.kind === 'person'),
    [chips],
  )

  // Resolve each group chip's members (for counts + the actual send list).
  const memberResults = useQueries({
    queries: groupChips.map((c) => groupMembersQueryOptions(c.id)),
  })

  const { recipients, total, textable, unreachable, groupsResolving } = useMemo(() => {
    const map = new Map<string, ResolvedRecipient>()
    let resolving = false
    groupChips.forEach((c, i) => {
      const res = memberResults[i]
      if (res?.isPending) resolving = true
      const members = (res?.data as UserData[] | undefined) ?? []
      members.forEach((m) => {
        const key = m.id || m.mobilePhone || m.displayName
        if (!map.has(key)) {
          map.set(key, {
            phoneNumber: m.mobilePhone || '',
            displayName: m.displayName,
            azureAdUserId: m.id,
            email: m.mail || undefined,
          })
        }
      })
    })
    personChips.forEach((c) => {
      const key = c.id || c.phone || c.name
      if (!map.has(key)) {
        map.set(key, {
          phoneNumber: c.phone || '',
          displayName: c.name,
          azureAdUserId: c.id,
          email: c.email || undefined,
        })
      }
    })
    const list = Array.from(map.values())
    // Reachability is judged the same way the server judges it, so "send to 14"
    // and the roster screen can't disagree about who the fourteen are.
    const unreachable = list.filter((r) => !canReceiveSMS(r.phoneNumber))
    return {
      recipients: list,
      total: list.length,
      textable: list.length - unreachable.length,
      unreachable,
      groupsResolving: resolving,
    }
  }, [groupChips, personChips, memberResults])

  const chosenGroupIds = useMemo(() => new Set(groupChips.map((c) => c.id)), [groupChips])
  const chosenPersonIds = useMemo(() => new Set(personChips.map((c) => c.id)), [personChips])

  const addGroup = (g: Group) => {
    setChips((prev) =>
      prev.some((c) => c.kind === 'group' && c.id === g.id)
        ? prev
        : [...prev, { kind: 'group', id: g.id, name: g.displayName }],
    )
    setQuery('')
  }

  const addPerson = (u: UserData) => {
    setChips((prev) =>
      prev.some((c) => c.kind === 'person' && c.id === u.id)
        ? prev
        : [
            ...prev,
            {
              kind: 'person',
              id: u.id,
              name: u.displayName,
              phone: u.mobilePhone ?? null,
              email: u.mail ?? null,
            },
          ],
    )
    setQuery('')
  }

  const removeChip = (chip: Chip) =>
    setChips((prev) => prev.filter((c) => !(c.kind === chip.kind && c.id === chip.id)))

  const clear = () => {
    setChips([])
    setQuery('')
    setSegment('all')
    setPickerOpen(false)
  }

  const groupCount = groupChips.length
  const peopleCount = personChips.length

  return {
    chips,
    query,
    setQuery,
    segment,
    setSegment,
    pickerOpen,
    setPickerOpen,
    groupChips,
    personChips,
    chosenGroupIds,
    chosenPersonIds,
    addGroup,
    addPerson,
    removeChip,
    clear,
    recipients,
    total,
    textable,
    /** Who a send would silently skip — no usable mobile number on file. */
    unreachable,
    groupsResolving,
    groupCount,
    peopleCount,
    hasChips: chips.length > 0,
    recipientLine: buildRecipientLine(total, groupCount, peopleCount),
  }
}

export type RecipientChips = ReturnType<typeof useRecipientChips>

/**
 * Recipients as roster rows, so the composer's "who's left out" list and the
 * Groups tab render people through exactly the same component.
 *
 * Everyone is marked as coming from the rule: at this point the chips have
 * already been flattened into one deduped list and provenance is no longer
 * knowable — nor useful, since these rows are only ever shown without it.
 */
export function asRosterMembers(recipients: Array<ResolvedRecipient>): Array<RosterMember> {
  return recipients.map((r) => ({
    id: r.azureAdUserId || r.phoneNumber || r.displayName,
    displayName: r.displayName,
    mail: r.email,
    hasMobile: canReceiveSMS(r.phoneNumber),
    source: 'rule' as const,
  }))
}
